import BillingRecoveryLayout from "@/layouts/billing_recovery_layout";
import { Link, router, usePage } from "@inertiajs/react";
import type { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { AppContent } from "@/components/app-content";
import { AppSidebar } from "@/components/app-sidebar";
import { AppShell } from "@/components/app-shell";
import { AppSidebarHeader } from "@/components/app-sidebar-header";
import { PageCanvas } from "@/components/page-canvas";
import { Button } from "@/components/ui/button";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import {
  activeSettingsItem,
  pathOnly,
  settingsGroups,
  locationSettingsGroups,
  isLocationSettings,
} from "@/lib/navigation";
import { userAccountClosurePath } from "@/routes";

export default function SettingsLayout({ children }: PropsWithChildren) {
  const { url, props } = usePage();
  const { t } = useTranslation();
  const local = isLocationSettings(url, props.auth.location?.id);
  const groups = local
    ? locationSettingsGroups(props.auth)
    : settingsGroups(props.auth);
  const heading = t(local ? "navigation.location_settings" : "common.settings");
  const root = local
    ? groups[0]?.items[0]?.href || "/user/profile"
    : "/user/profile";
  const active = activeSettingsItem(props.auth, url);
  const accountDanger =
    props.auth.user?.role === "admin" && pathOnly(url) === "/user/account" ? (
      <aside className="border-t pt-6" aria-label={t("navigation.danger")}>
        <Link
          href={userAccountClosurePath()}
          className="inline-flex min-h-11 items-center rounded-sm text-sm text-destructive underline underline-offset-4 focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t("recovery.delete_account")}
        </Link>
      </aside>
    ) : null;
  if (props.billing_access && !props.billing_access.allowed)
    return (
      <BillingRecoveryLayout>
        {children}
        {accountDanger}
      </BillingRecoveryLayout>
    );
  return (
    <AppShell>
      <AppSidebar />
      <AppContent>
        <AppSidebarHeader
          breadcrumbs={[
            { title: heading, href: root },
            ...(active ? [{ title: t(active.label), href: active.href }] : []),
          ]}
        />
        <PageCanvas className="flex-1">
          <div className="space-y-1 border-b pb-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {local ? props.auth.location?.name : props.auth.account?.name}
            </p>
            <p className="text-section font-semibold">{heading}</p>
          </div>
          <div className="w-full space-y-2 lg:hidden">
            <label htmlFor="settings-section" className="text-sm font-medium">
              {t("navigation.settings_sections")}
            </label>
            <NativeSelect
              id="settings-section"
              value={active?.href || ""}
              onChange={(event) => router.visit(event.target.value)}
            >
              {!active && (
                <NativeSelectOption value="" disabled>
                  {t("common.settings")}
                </NativeSelectOption>
              )}
              {groups.map((group) => (
                <optgroup
                  key={group.key}
                  label={t(`navigation.groups.${group.key}`)}
                >
                  {group.items.map((item) => (
                    <NativeSelectOption key={item.key} value={item.href}>
                      {t(item.label)}
                    </NativeSelectOption>
                  ))}
                </optgroup>
              ))}
            </NativeSelect>
          </div>
          <div className="flex min-w-0 flex-col items-start gap-6 lg:flex-row lg:gap-8">
            <nav
              className="hidden w-56 min-w-0 shrink-0 space-y-4 lg:block"
              aria-label={t("navigation.settings_sections")}
            >
              {groups.map((group) => (
                <section
                  key={group.key}
                  className="min-w-0 space-y-1"
                  aria-labelledby={`settings-${group.key}`}
                >
                  <h2
                    id={`settings-${group.key}`}
                    className="text-muted-foreground px-3 py-2 text-xs font-semibold uppercase tracking-wide"
                  >
                    {t(`navigation.groups.${group.key}`)}
                  </h2>
                  {group.items.map((item) => {
                    const selected = active?.href === item.href;
                    return (
                      <Button
                        key={item.key}
                        variant={selected ? "secondary" : "ghost"}
                        className="min-h-11 w-full justify-start text-left whitespace-normal"
                        render={
                          <Link
                            href={item.href}
                            aria-current={selected ? "page" : undefined}
                          />
                        }
                      >
                        {t(item.label)}
                      </Button>
                    );
                  })}
                </section>
              ))}
            </nav>
            <div className="w-full min-w-0 flex-1 space-y-8">
              {children}
              {accountDanger}
            </div>
          </div>
        </PageCanvas>
      </AppContent>
    </AppShell>
  );
}
